import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Target, Zap, Layers, DollarSign, Clock, Shield, ChevronDown, ChevronRight, Copy, Check, FileText, Database, Lock, Calendar, Mail, Download, ArrowRight, Server, FileSearch, CheckCircle2, AlertCircle } from 'lucide-react';
// --- DATA STRUCTURES ---
const STEPS = [{
  id: 1,
  title: 'Welcome'
}, {
  id: 2,
  title: 'Why This Matters'
}, {
  id: 3,
  title: 'How AI Will Work'
}, {
  id: 4,
  title: 'What RAG Is'
}, {
  id: 5,
  title: 'Data We Need'
}, {
  id: 6,
  title: 'Data Protection'
}, {
  id: 7,
  title: 'Workshop Agenda'
}, {
  id: 8,
  title: 'Final Handoff'
}];
const DATA_BUNDLES = [{
  id: 'bundle-a',
  title: 'Bundle A — Knowledge Base for RAG',
  items: ['Group sales SOP docs', 'Rate sheets + blackout calendars (last 3–6 months)', 'Concession menus + approval thresholds', 'Contract templates + key clauses (attrition/cancellation)', 'Internal FAQ / playbooks']
}, {
  id: 'bundle-b',
  title: 'Bundle B — Training Examples (RFP → quote → outcome)',
  items: ['200–500 historic inquiries (email text + attachments if available)', 'The quote sent (or final offer)', 'Outcome: won/lost + reason if known', 'Timestamps if available (time-to-quote, time-to-deposit)']
}, {
  id: 'bundle-c',
  title: 'Bundle C — Operational + Finance Signals (optional, valuable)',
  items: ['Monthly group volume by channel (direct vs wholesale) OR proxy', 'Commission/override totals tied to group channels (if available)', 'Rough hours per deal (before/after) OR workflow notes']
}];
const QUESTION_BANK = [{
  id: 'systems',
  label: 'Systems Map',
  questions: ['Where do group inquiries arrive today?', 'Which CRM/systems store group requests/quotes?', 'Where are rate sheets, blackouts, concessions stored?', 'Where are contracts stored?', 'Where is deposit status tracked?']
}, {
  id: 'exportability',
  label: 'Exportability',
  questions: ['Can you export last 12 months inquiries? Format?', 'Are quote revisions available?', 'Are outcomes (won/lost) available?', 'Is channel mix or proxy available?']
}, {
  id: 'rag',
  label: 'RAG Knowledge',
  questions: ['What are authoritative documents for policy, concessions, approvals and templates?', 'Are there different versions by property?', 'What are the most common team questions?']
}, {
  id: 'labels',
  label: 'Ground Truth Labels',
  questions: ['What is the "correct structured output" per inquiry?', 'Who can label 200 examples quickly?', 'What are the common error types today?']
}, {
  id: 'pilot',
  label: 'Pilot Onboarding',
  questions: ['Which properties + segment first?', 'How many agencies?', 'Who are the owners: IT, revenue, groups ops, finance?', 'What is the go-live date and success KPIs?']
}];
// --- REUSABLE COMPONENTS ---
const CopyButton = ({
  textToCopy,
  label = 'Copy'
}: {
  textToCopy: string;
  label?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <button onClick={handleCopy} className="flex items-center gap-2 px-3 py-1.5 border border-stone-700 bg-stone-900 hover:bg-stone-800 text-stone-300 transition-colors text-xs font-mono uppercase tracking-widest rounded-sm" data-id="element-1294">
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" data-id="element-1295" /> : <Copy className="w-3.5 h-3.5" data-id="element-1296" />}
      {copied ? 'Copied' : label}
    </button>;
};
const Accordion = ({
  title,
  children,
  defaultOpen = false,
  rightElement
}: any) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return <div className="border border-stone-800 bg-stone-900/30 overflow-hidden" data-id="element-1297">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 hover:bg-stone-800/50 transition-colors text-left" data-id="element-1298">
        <span className="font-medium text-stone-50" data-id="element-1299">{title}</span>
        <div className="flex items-center gap-4" data-id="element-1300">
          {rightElement && <div onClick={e => e.stopPropagation()} data-id="element-1301">{rightElement}</div>}
          <ChevronDown className={`w-5 h-5 text-stone-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1302" />
        </div>
      </button>
      <AnimatePresence initial={false} data-id="element-1303">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-1304">
            <div className="p-4 border-t border-stone-800" data-id="element-1305">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const CustomCheckbox = ({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) => <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1306">
    <div className={`mt-0.5 flex-shrink-0 w-5 h-5 border rounded-sm flex items-center justify-center transition-colors ${checked ? 'bg-red-600 border-red-600' : 'border-stone-600 group-hover:border-stone-400 bg-stone-900'}`} data-id="element-1307">
      {checked && <Check className="w-3.5 h-3.5 text-white" data-id="element-1308" />}
    </div>
    <span className={`text-sm leading-relaxed transition-colors ${checked ? 'text-stone-300 line-through opacity-70' : 'text-stone-300 group-hover:text-stone-50'}`} data-id="element-1309">
      {label}
    </span>
  </label>;
// --- MAIN PAGE COMPONENT ---
export function LomasOnboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [checkedItems, setCheckedItems] = useState({} as Record<string, boolean>);
  const [activeTab, setActiveTab] = useState(QUESTION_BANK[0].id);
  const [handoffForm, setHandoffForm] = useState({
    owners: '',
    dates: '',
    method: 'Secure SFTP',
    confirmedNoPII: false
  });
  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);
  const handleNext = () => {
    if (currentStep < 8) setCurrentStep(prev => prev + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };
  const toggleCheck = (item: string) => {
    setCheckedItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };
  const getBundleTextForCopy = (bundle: (typeof DATA_BUNDLES)[0]) => {
    return `${bundle.title}\n` + bundle.items.map(i => `- [ ] ${i}`).join('\n');
  };
  const getQuestionsForCopy = (tabId: string) => {
    const tab = QUESTION_BANK.find(t => t.id === tabId);
    if (!tab) return '';
    return `${tab.label} Questions:\n` + tab.questions.map(q => `- ${q}`).join('\n');
  };
  // --- STEP RENDERERS ---
  const renderStep1 = () => <div className="space-y-8" data-id="element-1310">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1311">
        <div className="p-6 border border-stone-800 bg-stone-900/30 hover:border-stone-700 transition-colors" data-id="element-1312">
          <Target className="w-8 h-8 text-red-600 mb-4" data-id="element-1313" />
          <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1314">
            Goal of the Pilot
          </h3>
          <p className="text-sm text-stone-400 leading-relaxed" data-id="element-1315">
            Prove speed, drive agency adoption, and deliver measurable margin
            impact within 90 days.
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30 hover:border-stone-700 transition-colors" data-id="element-1316">
          <Zap className="w-8 h-8 text-red-600 mb-4" data-id="element-1317" />
          <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1318">
            What BlockQuote Does
          </h3>
          <p className="text-sm text-stone-400 leading-relaxed" data-id="element-1319">
            Transforms group sales from email ping-pong into instant quotes →
            holds → contracts → deposits.
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30 hover:border-stone-700 transition-colors" data-id="element-1320">
          <Layers className="w-8 h-8 text-red-600 mb-4" data-id="element-1321" />
          <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1322">
            Building Together
          </h3>
          <p className="text-sm text-stone-400 leading-relaxed" data-id="element-1323">
            Deploying core infrastructure first, then layering Lomas-specific
            pricing intelligence over time.
          </p>
        </div>
      </div>
    </div>;
  const renderStep2 = () => <div className="space-y-8" data-id="element-1324">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1325">
        <div className="p-6 border border-stone-800 bg-stone-900/30 hover:border-stone-700 transition-colors" data-id="element-1326">
          <DollarSign className="w-8 h-8 text-red-600 mb-4" data-id="element-1327" />
          <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1328">
            Reduce Leakage
          </h3>
          <p className="text-sm text-stone-400 leading-relaxed" data-id="element-1329">
            Shift volume from wholesale to direct. Stop losing 15-25% margin on
            groups you already won.
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30 hover:border-stone-700 transition-colors" data-id="element-1330">
          <Clock className="w-8 h-8 text-red-600 mb-4" data-id="element-1331" />
          <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1332">
            Close Faster
          </h3>
          <p className="text-sm text-stone-400 leading-relaxed" data-id="element-1333">
            Drop speed-to-quote from 48 hours to 60 seconds. Win the business
            before competitors reply.
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30 hover:border-stone-700 transition-colors" data-id="element-1334">
          <Shield className="w-8 h-8 text-red-600 mb-4" data-id="element-1335" />
          <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1336">
            Protect Margin
          </h3>
          <p className="text-sm text-stone-400 leading-relaxed" data-id="element-1337">
            Enforce rate floors, blackout dates, and concession limits
            automatically across all quotes.
          </p>
        </div>
      </div>

      <Accordion title="Other operational advantages" data-id="element-1338">
        <ul className="space-y-3 text-sm text-stone-400" data-id="element-1339">
          <li className="flex items-start gap-2" data-id="element-1340">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5" data-id="element-1341" /> 60%+ reduction
            in manual quoting hours for the sales team
          </li>
          <li className="flex items-start gap-2" data-id="element-1342">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5" data-id="element-1343" /> Fewer human
            errors in pricing and inventory checks
          </li>
          <li className="flex items-start gap-2" data-id="element-1344">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5" data-id="element-1345" /> Standardized,
            branded proposals generated instantly
          </li>
          <li className="flex items-start gap-2" data-id="element-1346">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5" data-id="element-1347" /> Full audit
            trail of every quote, revision, and approval
          </li>
        </ul>
      </Accordion>
    </div>;
  const renderStep3 = () => <div className="space-y-8" data-id="element-1348">
      <div className="grid grid-cols-1 gap-4" data-id="element-1349">
        <div className="p-6 border border-stone-800 bg-stone-900/30 flex gap-6 items-start" data-id="element-1350">
          <div className="w-12 h-12 rounded-full bg-stone-900 border border-stone-700 flex items-center justify-center flex-shrink-0 font-mono text-lg text-stone-300" data-id="element-1351">
            01
          </div>
          <div data-id="element-1352">
            <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1353">
              Phase 1: Intake Automation
            </h3>
            <p className="text-stone-400" data-id="element-1354">
              AI parses messy email RFPs and agency requests into structured
              fields (dates, rooms, events) instantly.
            </p>
          </div>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30 flex gap-6 items-start" data-id="element-1355">
          <div className="w-12 h-12 rounded-full bg-stone-900 border border-stone-700 flex items-center justify-center flex-shrink-0 font-mono text-lg text-stone-300" data-id="element-1356">
            02
          </div>
          <div data-id="element-1357">
            <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1358">
              Phase 2: Guardrails Copilot
            </h3>
            <p className="text-stone-400" data-id="element-1359">
              AI flags policy violations, suggests concessions, and provides
              explanations. Hard rules always override AI.
            </p>
          </div>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30 flex gap-6 items-start" data-id="element-1360">
          <div className="w-12 h-12 rounded-full bg-stone-900 border border-stone-700 flex items-center justify-center flex-shrink-0 font-mono text-lg text-stone-300" data-id="element-1361">
            03
          </div>
          <div data-id="element-1362">
            <h3 className="font-serif text-xl text-stone-50 mb-2" data-id="element-1363">
              Phase 3: Recommendations
            </h3>
            <p className="text-stone-400" data-id="element-1364">
              AI suggests optimal Good/Better/Best bundles and calculates win
              probability, bounded strictly by Lomas policy.
            </p>
          </div>
        </div>
      </div>

      <div className="border-l-4 border-amber-500 bg-amber-950/20 p-6 flex gap-4 items-start" data-id="element-1365">
        <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" data-id="element-1366" />
        <div data-id="element-1367">
          <h4 className="font-medium text-amber-500 mb-1" data-id="element-1368">
            AI never invents rates.
          </h4>
          <p className="text-sm text-amber-200/70" data-id="element-1369">
            BlockQuote.ai uses deterministic logic for pricing. Rates,
            inventory, and floors come directly from Lomas rate sheets and
            policy configurations. AI is used for parsing, matching, and
            recommendations—not math.
          </p>
        </div>
      </div>
    </div>;
  const renderStep4 = () => <div className="space-y-8" data-id="element-1370">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1371">
        <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-1372">
          <FileSearch className="w-6 h-6 text-stone-400 mb-4" data-id="element-1373" />
          <h3 className="font-serif text-lg text-stone-50 mb-2" data-id="element-1374">
            What RAG Means
          </h3>
          <p className="text-sm text-stone-400" data-id="element-1375">
            Retrieval-Augmented Generation. The AI searches your specific
            documents to find answers, then cites its sources.
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-1376">
          <Database className="w-6 h-6 text-stone-400 mb-4" data-id="element-1377" />
          <h3 className="font-serif text-lg text-stone-50 mb-2" data-id="element-1378">
            What Goes In
          </h3>
          <p className="text-sm text-stone-400" data-id="element-1379">
            SOPs, group policies, concession menus, contract templates, and rate
            logic rules.
          </p>
        </div>
        <div className="p-6 border border-red-900/50 bg-red-950/10" data-id="element-1380">
          <Lock className="w-6 h-6 text-red-500 mb-4" data-id="element-1381" />
          <h3 className="font-serif text-lg text-red-50 mb-2" data-id="element-1382">
            What Stays Out
          </h3>
          <p className="text-sm text-red-200/70" data-id="element-1383">
            Guest PII, passport copies, credit card data, and private personal
            identifiers. Do not upload these.
          </p>
        </div>
      </div>

      <div className="mt-12 p-8 border border-stone-800 bg-stone-900/20 rounded-lg overflow-x-auto" data-id="element-1384">
        <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6 text-center" data-id="element-1385">
          How RAG Answers Questions
        </p>
        <div className="flex items-center justify-between min-w-[600px] gap-4 text-sm font-mono" data-id="element-1386">
          <div className="flex flex-col items-center gap-3 flex-1" data-id="element-1387">
            <div className="h-16 w-full border border-stone-700 bg-stone-900 flex items-center justify-center text-stone-300 rounded-sm" data-id="element-1388">
              Lomas Docs
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-stone-600 flex-shrink-0" data-id="element-1389" />
          <div className="flex flex-col items-center gap-3 flex-1" data-id="element-1390">
            <div className="h-16 w-full border border-stone-700 bg-stone-900 flex items-center justify-center text-stone-300 rounded-sm text-center px-2" data-id="element-1391">
              Chunking +<br data-id="element-1392" />
              Metadata
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-stone-600 flex-shrink-0" data-id="element-1393" />
          <div className="flex flex-col items-center gap-3 flex-1" data-id="element-1394">
            <div className="h-16 w-full border border-stone-700 bg-stone-900 flex items-center justify-center text-stone-300 rounded-sm" data-id="element-1395">
              Retrieval
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-1396" />
          <div className="flex flex-col items-center gap-3 flex-1" data-id="element-1397">
            <div className="h-16 w-full border border-red-900/50 bg-red-950/30 flex items-center justify-center text-red-400 rounded-sm text-center px-2" data-id="element-1398">
              Answer w/
              <br data-id="element-1399" />
              Citations
            </div>
          </div>
        </div>
      </div>
    </div>;
  const renderStep5 = () => <div className="space-y-6" data-id="element-1400">
      {DATA_BUNDLES.map(bundle => <Accordion key={bundle.id} title={bundle.title} defaultOpen={true} rightElement={<CopyButton textToCopy={getBundleTextForCopy(bundle)} label="Copy List" data-id="element-1402" />} data-id="element-1401">
          <div className="space-y-4 pt-2" data-id="element-1403">
            {bundle.items.map((item, idx) => <CustomCheckbox key={idx} label={item} checked={!!checkedItems[`${bundle.id}-${idx}`]} onChange={() => toggleCheck(`${bundle.id}-${idx}`)} data-id="element-1404" />)}
          </div>
        </Accordion>)}

      <div className="mt-8 p-6 border border-stone-800 bg-stone-900/30" data-id="element-1405">
        <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-1406">
          Preferred Formats & Delivery
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1407">
          <div data-id="element-1408">
            <p className="text-stone-50 font-medium mb-1 text-sm" data-id="element-1409">
              Structured Data
            </p>
            <p className="text-stone-400 text-sm" data-id="element-1410">CSV or Excel exports</p>
          </div>
          <div data-id="element-1411">
            <p className="text-stone-50 font-medium mb-1 text-sm" data-id="element-1412">
              Policy Docs
            </p>
            <p className="text-stone-400 text-sm" data-id="element-1413">PDF, Word, or plain text</p>
          </div>
          <div data-id="element-1414">
            <p className="text-stone-50 font-medium mb-1 text-sm" data-id="element-1415">
              Delivery Method
            </p>
            <p className="text-stone-400 text-sm" data-id="element-1416">
              Shared Drive folder or Secure SFTP
            </p>
          </div>
        </div>
      </div>
    </div>;
  const renderStep6 = () => <div className="space-y-8" data-id="element-1417">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1418">
        <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-1419">
          <Server className="w-6 h-6 text-stone-400 mb-4" data-id="element-1420" />
          <h3 className="font-serif text-lg text-stone-50 mb-2" data-id="element-1421">
            Tenant Isolation
          </h3>
          <p className="text-sm text-stone-400" data-id="element-1422">
            Lomas data is logically separated from all other resort groups. No
            cross-contamination.
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-1423">
          <Shield className="w-6 h-6 text-stone-400 mb-4" data-id="element-1424" />
          <h3 className="font-serif text-lg text-stone-50 mb-2" data-id="element-1425">PII Removal</h3>
          <p className="text-sm text-stone-400" data-id="element-1426">
            Names, emails, phones, and passports are scrubbed at ingestion
            before hitting any AI model.
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-1427">
          <Lock className="w-6 h-6 text-stone-400 mb-4" data-id="element-1428" />
          <h3 className="font-serif text-lg text-stone-50 mb-2" data-id="element-1429">Encryption</h3>
          <p className="text-sm text-stone-400" data-id="element-1430">
            All data is encrypted in transit (TLS 1.3) and at rest (AES-256).
          </p>
        </div>
        <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-1431">
          <FileText className="w-6 h-6 text-stone-400 mb-4" data-id="element-1432" />
          <h3 className="font-serif text-lg text-stone-50 mb-2" data-id="element-1433">
            Data Ownership
          </h3>
          <p className="text-sm text-stone-400" data-id="element-1434">
            Lomas owns the raw data. BlockQuote only uses it to operate the
            service and improve models per our agreement.
          </p>
        </div>
      </div>

      <Accordion title="Anonymization Rules (Examples)" data-id="element-1435">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono text-sm" data-id="element-1436">
          <div className="p-4 bg-stone-900 border border-stone-800 flex items-center justify-between" data-id="element-1437">
            <span className="text-stone-400" data-id="element-1438">"John Smith"</span>
            <ArrowRight className="w-4 h-4 text-stone-600" data-id="element-1439" />
            <span className="text-emerald-500" data-id="element-1440">GUEST_1</span>
          </div>
          <div className="p-4 bg-stone-900 border border-stone-800 flex items-center justify-between" data-id="element-1441">
            <span className="text-stone-400" data-id="element-1442">"Agency ABC"</span>
            <ArrowRight className="w-4 h-4 text-stone-600" data-id="element-1443" />
            <span className="text-emerald-500" data-id="element-1444">AGENCY_12</span>
          </div>
          <div className="p-4 bg-stone-900 border border-stone-800 flex items-center justify-between" data-id="element-1445">
            <span className="text-stone-400" data-id="element-1446">"john@email.com"</span>
            <ArrowRight className="w-4 h-4 text-stone-600" data-id="element-1447" />
            <span className="text-emerald-500" data-id="element-1448">[EMAIL_REDACTED]</span>
          </div>
          <div className="p-4 bg-stone-900 border border-stone-800 flex items-center justify-between" data-id="element-1449">
            <span className="text-stone-400" data-id="element-1450">"+1 555-0198"</span>
            <ArrowRight className="w-4 h-4 text-stone-600" data-id="element-1451" />
            <span className="text-emerald-500" data-id="element-1452">[PHONE_REDACTED]</span>
          </div>
        </div>
      </Accordion>
    </div>;
  const renderStep7 = () => <div className="space-y-10" data-id="element-1453">
      <div data-id="element-1454">
        <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1455">
          Agenda Timeline
        </h3>
        <div className="space-y-4" data-id="element-1456">
          {[{
          time: '0–10 min',
          title: 'Intros + Goals'
        }, {
          time: '10–30 min',
          title: 'Systems Map Walkthrough'
        }, {
          time: '30–55 min',
          title: 'Data Availability Deep-Dive'
        }, {
          time: '55–75 min',
          title: 'RAG Knowledge + Labeling Plan'
        }, {
          time: '75–90 min',
          title: 'Pilot Scope + Next Steps'
        }].map((item, i) => <div key={i} className="flex items-center gap-6 p-4 border border-stone-800 bg-stone-900/30" data-id="element-1457">
              <div className="w-24 font-mono text-sm text-red-500 flex-shrink-0" data-id="element-1458">
                {item.time}
              </div>
              <div className="w-px h-8 bg-stone-800 hidden md:block" data-id="element-1459"></div>
              <div className="text-stone-50 font-medium" data-id="element-1460">{item.title}</div>
            </div>)}
        </div>
      </div>

      <div data-id="element-1461">
        <div className="flex items-center justify-between mb-6" data-id="element-1462">
          <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-1463">
            Question Bank
          </h3>
          <CopyButton textToCopy={getQuestionsForCopy(activeTab)} label="Copy Questions" data-id="element-1464" />
        </div>

        <div className="border border-stone-800 bg-stone-900/30" data-id="element-1465">
          <div className="flex overflow-x-auto border-b border-stone-800 hide-scrollbar" data-id="element-1466">
            {QUESTION_BANK.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors relative ${activeTab === tab.id ? 'text-stone-50' : 'text-stone-400 hover:text-stone-300'}`} data-id="element-1467">
                {tab.label}
                {activeTab === tab.id && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-600" data-id="element-1468" />}
              </button>)}
          </div>
          <div className="p-6" data-id="element-1469">
            <AnimatePresence mode="wait" data-id="element-1470">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -5
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-1471">
                {QUESTION_BANK.find(t => t.id === activeTab)?.questions.map((q, i) => <div key={i} className="flex gap-4 items-start" data-id="element-1472">
                      <div className="w-6 h-6 rounded-full bg-stone-800 flex items-center justify-center flex-shrink-0 text-xs text-stone-400 mt-0.5" data-id="element-1473">
                        {i + 1}
                      </div>
                      <p className="text-stone-300 leading-relaxed" data-id="element-1474">{q}</p>
                    </div>)}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
  const renderStep8 = () => {
    // Count checked items
    const checkedCount = Object.values(checkedItems).filter(Boolean).length;
    const totalItems = DATA_BUNDLES.reduce((acc, bundle) => acc + bundle.items.length, 0);
    return <div className="space-y-10" data-id="element-1475">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1476">
          {/* Left Column: Review & Form */}
          <div className="space-y-8" data-id="element-1477">
            <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-1478">
              <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4 flex items-center justify-between" data-id="element-1479">
                <span data-id="element-1480">Data Checklist Status</span>
                <span className={checkedCount === totalItems ? 'text-emerald-500' : 'text-amber-500'} data-id="element-1481">
                  {checkedCount} / {totalItems} Selected
                </span>
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar" data-id="element-1482">
                {DATA_BUNDLES.flatMap(b => b.items).map((item, i) => {
                // Find the original key
                let key = '';
                DATA_BUNDLES.forEach(b => {
                  const idx = b.items.indexOf(item);
                  if (idx !== -1) key = `${b.id}-${idx}`;
                });
                const isChecked = !!checkedItems[key];
                return <div key={i} className="flex items-start gap-2 text-sm" data-id="element-1483">
                      {isChecked ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-1484" /> : <div className="w-4 h-4 border border-stone-700 rounded-sm mt-0.5 flex-shrink-0" data-id="element-1485" />}
                      <span className={isChecked ? 'text-stone-300' : 'text-stone-500'} data-id="element-1486">
                        {item}
                      </span>
                    </div>;
              })}
              </div>
            </div>

            <div className="space-y-5" data-id="element-1487">
              <div data-id="element-1488">
                <label className="block font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-1489">
                  Named Owners
                </label>
                <input type="text" placeholder="e.g. Maria (Revenue), David (IT)" className="w-full bg-stone-900 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors" value={handoffForm.owners} onChange={e => setHandoffForm({
                ...handoffForm,
                owners: e.target.value
              })} data-id="element-1490" />
              </div>
              <div data-id="element-1491">
                <label className="block font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-1492">
                  Target Delivery Date
                </label>
                <input type="text" placeholder="e.g. Oct 15th" className="w-full bg-stone-900 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors" value={handoffForm.dates} onChange={e => setHandoffForm({
                ...handoffForm,
                dates: e.target.value
              })} data-id="element-1493" />
              </div>
              <div data-id="element-1494">
                <label className="block font-mono text-xs uppercase tracking-widest text-stone-500 mb-2" data-id="element-1495">
                  Delivery Method
                </label>
                <select className="w-full bg-stone-900 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors appearance-none" value={handoffForm.method} onChange={e => setHandoffForm({
                ...handoffForm,
                method: e.target.value
              })} data-id="element-1496">
                  <option data-id="element-1497">Secure SFTP</option>
                  <option data-id="element-1498">Shared Google Drive</option>
                  <option data-id="element-1499">Shared OneDrive</option>
                  <option data-id="element-1500">Encrypted Email</option>
                </select>
              </div>

              <div className="pt-4" data-id="element-1501">
                <CustomCheckbox label="I confirm no guest PII (passports, CCs) will be included in the data transfer." checked={handoffForm.confirmedNoPII} onChange={() => setHandoffForm({
                ...handoffForm,
                confirmedNoPII: !handoffForm.confirmedNoPII
              })} data-id="element-1502" />
              </div>
            </div>
          </div>

          {/* Right Column: Actions */}
          <div data-id="element-1503">
            <div className="sticky top-8 space-y-4" data-id="element-1504">
              <h3 className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-1505">
                Next Actions
              </h3>

              <button className="w-full flex items-center justify-between p-4 border border-stone-800 bg-stone-900/30 hover:bg-stone-800 transition-colors group" data-id="element-1506">
                <div className="flex items-center gap-3" data-id="element-1507">
                  <Download className="w-5 h-5 text-stone-400 group-hover:text-stone-50 transition-colors" data-id="element-1508" />
                  <span className="font-medium text-stone-300 group-hover:text-stone-50 transition-colors" data-id="element-1509">
                    Export Summary (PDF)
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-600 group-hover:text-stone-400" data-id="element-1510" />
              </button>

              <button onClick={() => {
              navigator.clipboard.writeText('Lomas Group Onboarding Summary\n\nOwners: ' + handoffForm.owners + '\nDate: ' + handoffForm.dates);
              alert('Summary copied to clipboard!');
            }} className="w-full flex items-center justify-between p-4 border border-stone-800 bg-stone-900/30 hover:bg-stone-800 transition-colors group" data-id="element-1511">
                <div className="flex items-center gap-3" data-id="element-1512">
                  <Copy className="w-5 h-5 text-stone-400 group-hover:text-stone-50 transition-colors" data-id="element-1513" />
                  <span className="font-medium text-stone-300 group-hover:text-stone-50 transition-colors" data-id="element-1514">
                    Copy Summary
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-600 group-hover:text-stone-400" data-id="element-1515" />
              </button>

              <button className="w-full flex items-center justify-between p-4 border border-stone-800 bg-stone-900/30 hover:bg-stone-800 transition-colors group" data-id="element-1516">
                <div className="flex items-center gap-3" data-id="element-1517">
                  <Calendar className="w-5 h-5 text-stone-400 group-hover:text-stone-50 transition-colors" data-id="element-1518" />
                  <span className="font-medium text-stone-300 group-hover:text-stone-50 transition-colors" data-id="element-1519">
                    Book Workshop
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-600 group-hover:text-stone-400" data-id="element-1520" />
              </button>

              <button className="w-full flex items-center justify-between p-4 border border-red-900/50 bg-red-600 hover:bg-red-700 transition-colors group" data-id="element-1521">
                <div className="flex items-center gap-3" data-id="element-1522">
                  <Mail className="w-5 h-5 text-white" data-id="element-1523" />
                  <span className="font-medium text-white" data-id="element-1524">
                    Send to BlockQuote Team
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-red-200" data-id="element-1525" />
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      case 8:
        return renderStep8();
      default:
        return null;
    }
  };
  const getStepSubtitle = (step: number) => {
    switch (step) {
      case 1:
        return 'Everything you need to prepare for a successful pilot launch.';
      case 2:
        return 'Three measurable outcomes that justify the pilot.';
      case 3:
        return 'AI is introduced in controlled phases. Rules always decide.';
      case 4:
        return 'Your documents power the AI — nothing is fabricated.';
      case 5:
        return 'Three categories of data, each with a clear checklist.';
      case 6:
        return 'Your data is isolated, encrypted and under your control.';
      case 7:
        return 'A structured workshop to map your systems and data.';
      case 8:
        return 'Review, assign owners and confirm delivery.';
      default:
        return '';
    }
  };
  return <div className="min-h-screen bg-stone-950 text-stone-50 font-sans flex flex-col selection:bg-red-600/30" data-id="element-1526">
      {/* Top Banner */}
      <div className="h-12 bg-stone-900 border-b border-stone-800 flex items-center px-6 relative z-20" data-id="element-1527">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-600" data-id="element-1528"></div>
        <div className="flex items-center gap-3" data-id="element-1529">
          <div className="w-5 h-5 bg-red-600 flex items-center justify-center" data-id="element-1530">
            <span className="font-serif text-xs font-bold text-white" data-id="element-1531">B</span>
          </div>
          <span className="font-mono text-xs uppercase tracking-widest text-stone-400" data-id="element-1532">
            Pilot onboarding — secure, scoped, and measurable
          </span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden" data-id="element-1533">
        {/* Left Sidebar */}
        <aside className="w-72 border-r border-stone-800 bg-stone-950 flex-shrink-0 flex flex-col hidden md:flex" data-id="element-1534">
          <div className="p-6 border-b border-stone-800" data-id="element-1535">
            <Link to="/" className="inline-flex items-center text-sm font-mono uppercase tracking-widest text-stone-500 hover:text-stone-300 transition-colors" data-id="element-1536">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1537" />
              Back to site
            </Link>
          </div>
          <div className="p-6 flex-1 overflow-y-auto" data-id="element-1538">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-600 mb-6" data-id="element-1539">
              Onboarding Steps
            </p>
            <nav className="space-y-1 relative" data-id="element-1540">
              {/* Vertical connecting line */}
              <div className="absolute left-[11px] top-4 bottom-4 w-px bg-stone-800 z-0" data-id="element-1541"></div>

              {STEPS.map(step => {
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              return <button key={step.id} onClick={() => setCurrentStep(step.id)} className="w-full flex items-start gap-4 py-3 relative z-10 group text-left" data-id="element-1542">
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-red-600 text-white' : isCompleted ? 'bg-emerald-500 text-white' : 'bg-stone-900 border border-stone-700 text-stone-500 group-hover:border-stone-500'}`} data-id="element-1543">
                      {isCompleted ? <Check className="w-3.5 h-3.5" data-id="element-1544" /> : <span className="text-xs font-mono" data-id="element-1545">{step.id}</span>}
                    </div>
                    <span className={`text-sm font-medium transition-colors ${isActive ? 'text-stone-50' : isCompleted ? 'text-stone-300' : 'text-stone-500 group-hover:text-stone-300'}`} data-id="element-1546">
                      {step.title}
                    </span>
                  </button>;
            })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto relative bg-stone-950" data-id="element-1547">
          <div className="max-w-4xl mx-auto px-8 py-16 md:px-16 md:py-24 min-h-full flex flex-col" data-id="element-1548">
            {/* Header */}
            <div className="mb-12" data-id="element-1549">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-4" data-id="element-1550">
                Step {currentStep} of 8
              </p>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 mb-4" data-id="element-1551">
                {STEPS[currentStep - 1].title}
              </h1>
              <p className="text-xl text-stone-400" data-id="element-1552">
                {getStepSubtitle(currentStep)}
              </p>
            </div>

            {/* Content Area */}
            <div className="flex-1" data-id="element-1553">
              <AnimatePresence mode="wait" data-id="element-1554">
                <motion.div key={currentStep} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} data-id="element-1555">
                  {renderCurrentStep()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Buttons */}
            <div className="mt-16 pt-8 border-t border-stone-800 flex items-center justify-between" data-id="element-1556">
              <button onClick={handleBack} className={`px-6 py-3 border border-stone-700 text-stone-300 hover:text-stone-50 hover:bg-stone-900 transition-colors font-medium text-sm ${currentStep === 1 ? 'opacity-0 pointer-events-none' : ''}`} data-id="element-1557">
                Back
              </button>

              <button onClick={handleNext} className={`px-8 py-3 bg-red-600 hover:bg-red-700 text-white transition-colors font-medium text-sm flex items-center gap-2 ${currentStep === 8 ? 'opacity-0 pointer-events-none' : ''}`} data-id="element-1558">
                Proceed to Step {currentStep + 1}
                <ArrowRight className="w-4 h-4" data-id="element-1559" />
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>;
}